/* eslint-disable react/prop-types */
import { Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase';
import { signInWithGoogle } from '../lib/auth';
import { authorName, ensureUserDoc, needsUsername, watchUser } from '../lib/user';
import { watchUnreadCount } from '../lib/notifications';
import Avatar from './ui/Avatar';
import Button from './ui/Button';
import ThemeToggle from './ThemeToggle';
import UsernameClaimModal from './UsernameClaimModal';

// SVG path children are composed by the icon components below.
function Icon({ children }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
      {children}
    </svg>
  );
}

function HomeIcon() {
  return <Icon><path d="m3 10 9-7 9 7v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1Z" /><path d="M9 21v-7h6v7" /></Icon>;
}

function JournalIcon() {
  return <Icon><path d="M3 5.5A2.5 2.5 0 0 1 5.5 3H11v16H5.5A2.5 2.5 0 0 0 3 21.5Z" /><path d="M21 5.5A2.5 2.5 0 0 0 18.5 3H13v16h5.5a2.5 2.5 0 0 1 2.5 2.5Z" /></Icon>;
}

function CommunityIcon() {
  return <Icon><circle cx="9" cy="8" r="3" /><circle cx="17" cy="9" r="2.5" /><path d="M3.5 20a5.5 5.5 0 0 1 11 0" /><path d="M14 15.5a4.5 4.5 0 0 1 6.5 4" /></Icon>;
}

function TwinIcon() {
  return <Icon><circle cx="9" cy="12" r="5.5" /><circle cx="15" cy="12" r="5.5" /></Icon>;
}

function ProfileIcon() {
  return <Icon><circle cx="12" cy="8" r="3.5" /><path d="M4.5 21a7.5 7.5 0 0 1 15 0" /></Icon>;
}

function AboutIcon() {
  return <Icon><circle cx="12" cy="12" r="9" /><path d="M12 11v5" /><path d="M12 8h.01" /></Icon>;
}

function FeedbackIcon() {
  return <Icon><path d="M4 5.5A2.5 2.5 0 0 1 6.5 3h11A2.5 2.5 0 0 1 20 5.5v7a2.5 2.5 0 0 1-2.5 2.5H11l-4.5 4v-4.3A2.5 2.5 0 0 1 4 12.5Z" /><path d="M8 8.5h8M8 11.5h5" /></Icon>;
}

function BellIcon() {
  return <Icon><path d="M18 10a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" /><path d="M10 22h4" /></Icon>;
}

function SearchIcon() {
  return <Icon><circle cx="11" cy="11" r="6.5" /><path d="m20 20-3.5-3.5" /></Icon>;
}

const links = [
  ['/', 'Home', HomeIcon],
  ['/journal', 'Journal', JournalIcon],
  ['/community', 'Community', CommunityIcon],
  ['/twin', 'Loc Twin', TwinIcon],
  ['/profile', 'Profile', ProfileIcon],
  ['/feedback', 'Feedback', FeedbackIcon],
  ['/about', 'About', AboutIcon],
];

function playPageSectionEnter(el) {
  if (!el) return;
  el.classList.remove('page-section-enter');
  el.classList.add('page-section-enter-pre');
  void el.offsetHeight;
  el.classList.remove('page-section-enter-pre');
  el.classList.add('page-section-enter');
}

function AnimatedOutlet({ containerRef }) {
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const id = requestAnimationFrame(() => {
      playPageSectionEnter(el);
    });
    return () => cancelAnimationFrame(id);
  }, [containerRef]);

  return <Outlet />;
}

function PageSection() {
  const { pathname } = useLocation();
  const ref = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    el.classList.remove('page-section-enter');
    el.classList.add('page-section-enter-pre');
    return () => {
      el.classList.remove('page-section-enter', 'page-section-enter-pre');
    };
  }, [pathname]);

  return (
    <div ref={ref} className="page-section-enter-pre">
      <Suspense fallback={null}>
        <AnimatedOutlet key={pathname} containerRef={ref} />
      </Suspense>
    </div>
  );
}

export default function AppShell() {
  const [user] = useAuthState(auth);
  const [profile, setProfile] = useState(null);
  const [profileReady, setProfileReady] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [search, setSearch] = useState('');
  const [chromeEnter, setChromeEnter] = useState(false);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const id = requestAnimationFrame(() => setChromeEnter(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    if (!user) {
      setProfile(null);
      setProfileReady(false);
      return undefined;
    }
    ensureUserDoc(user).catch(() => {});
    return watchUser(user.uid, (data) => {
      setProfile(data);
      setProfileReady(true);
    });
  }, [user]);

  useEffect(() => {
    if (!user) {
      setUnread(0);
      return undefined;
    }
    return watchUnreadCount(user.uid, setUnread, { onError: () => setUnread(0) });
  }, [user]);

  const showUsernamePrompt = Boolean(user && profileReady && needsUsername(profile));

  const signIn = () => signInWithGoogle().catch(() => {});
  const submit = (event) => {
    event.preventDefault();
    if (search.trim()) navigate(`/community?q=${encodeURIComponent(search.trim())}`);
  };

  return (
    <div className="min-h-screen bg-paper">
      <aside className={`app-sidebar group/sidebar fixed inset-y-0 left-0 z-10 flex w-[250px] -translate-x-full flex-col overflow-hidden border-r border-parchment bg-parchment px-4 py-8 transition-transform lg:w-auto lg:translate-x-0 lg:px-2 lg:py-6 lg:hover:px-4 ${drawer ? 'translate-x-0' : ''} ${chromeEnter ? 'app-shell-chrome-enter' : 'app-shell-chrome-enter-pre'}`}>
        <NavLink
          className="max-h-none px-4 pb-[38px] pt-2 opacity-100 transition-[opacity,max-height,padding] duration-[250ms] ease-out max-lg:block lg:max-h-0 lg:overflow-hidden lg:pb-0 lg:pt-0 lg:opacity-0 lg:group-hover/sidebar:max-h-24 lg:group-hover/sidebar:pb-[38px] lg:group-hover/sidebar:pt-2 lg:group-hover/sidebar:opacity-100"
          to="/"
          onClick={() => setDrawer(false)}
        >
          <strong className="block font-serif text-[28px] font-bold leading-none text-umber">Loc&apos;d In</strong>
          <span className="mt-2 block text-[10px] font-extrabold uppercase tracking-[0.14em] text-subtle">Organic Growth</span>
        </NavLink>
        <nav className="grid gap-1.5">
          {links.map(([to, label, LinkIcon]) => (
            <NavLink
              key={to}
              end={to === '/'}
              to={to}
              aria-label={label}
              onClick={() => setDrawer(false)}
              className={({ isActive }) => `relative flex items-center gap-[13px] rounded-lg px-4 py-[13px] font-bold transition-[background-color,color,gap,padding] duration-[250ms] ease-out motion-reduce:transition-none text-subtle after:pointer-events-none after:absolute after:-right-4 after:top-1/2 after:h-7 after:w-[3px] after:-translate-y-1/2 after:rounded after:bg-umber after:transition-[opacity,transform] after:duration-[250ms] after:ease-out after:content-[''] motion-reduce:after:transition-none lg:justify-center lg:gap-0 lg:px-3 lg:group-hover/sidebar:justify-start lg:group-hover/sidebar:gap-[13px] lg:group-hover/sidebar:px-4 ${isActive ? `bg-container text-umber after:scale-y-100 after:opacity-100 lg:after:opacity-0 lg:group-hover/sidebar:after:opacity-100` : 'after:scale-y-50 after:opacity-0'}`}
            >
              <i className="w-5 shrink-0 text-center not-italic"><LinkIcon /></i>
              <span className="overflow-hidden whitespace-nowrap transition-[opacity,max-width] duration-[250ms] ease-out max-lg:max-w-none max-lg:opacity-100 lg:max-w-0 lg:opacity-0 lg:group-hover/sidebar:max-w-[120px] lg:group-hover/sidebar:opacity-100">{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="mx-2 mb-2 mt-auto transition-[margin,opacity] duration-[250ms] ease-out lg:mx-0 lg:opacity-100 lg:group-hover/sidebar:mx-2">
          {user && (
            <Button
              aria-label="New Entry"
              className="w-full lg:px-0 lg:group-hover/sidebar:px-[1.125rem]"
              onClick={() => navigate('/journal/add')}
            >
              <span className="lg:hidden">＋ New Entry</span>
              <span aria-hidden="true" className="hidden lg:inline lg:group-hover/sidebar:hidden">＋</span>
              <span className="hidden lg:group-hover/sidebar:inline">＋ New Entry</span>
            </Button>
          )}
          <div className="mt-3 flex justify-center gap-3 overflow-hidden text-xs text-subtle transition-[opacity,max-height] duration-[250ms] ease-out max-lg:max-h-none max-lg:opacity-100 lg:max-h-0 lg:opacity-0 lg:pointer-events-none lg:group-hover/sidebar:max-h-8 lg:group-hover/sidebar:opacity-100 lg:group-hover/sidebar:pointer-events-auto">
            <NavLink className="text-subtle" to="/privacy" onClick={() => setDrawer(false)}>Privacy</NavLink>
            <NavLink className="text-subtle" to="/terms" onClick={() => setDrawer(false)}>Terms</NavLink>
            <a className="text-subtle" href="https://redcodestudios.com" target="_blank" rel="noopener noreferrer">Redcode Studios</a>
          </div>
        </div>
      </aside>
      {drawer && <button className="fixed inset-0 z-[9] border-0 bg-black/25 lg:hidden" aria-label="Close menu" onClick={() => setDrawer(false)} />}
      <section className="app-main min-w-0">
        <header className={`sticky top-0 z-[5] flex h-[76px] items-center gap-2 border-b border-parchment bg-paper px-4 sm:gap-3.5 sm:px-8 ${chromeEnter ? 'app-shell-chrome-enter' : 'app-shell-chrome-enter-pre'}`}>
          <button className="border-0 bg-transparent text-2xl lg:hidden" aria-label="Open menu" onClick={() => setDrawer(true)}>☰</button>
          <form className="flex max-w-[480px] flex-1 items-center gap-2 rounded-full bg-container px-4 py-[9px] text-subtle" onSubmit={submit}>
            <span className="shrink-0"><SearchIcon /></span>
            <input className="min-w-0 w-full border-0 bg-transparent outline-none" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search…" />
          </form>
          <div className="ml-auto flex shrink-0 items-center gap-2 sm:gap-3.5">
            <button
              className="relative border-0 bg-transparent"
              aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
              onClick={() => navigate(user ? '/notifications' : '/login')}
            >
              <BellIcon />
              {unread > 0 && (
                <span className="absolute -right-1 -top-1 grid min-w-[16px] place-content-center rounded-full bg-accent px-1 text-[10px] font-bold leading-4 text-white">
                  {unread > 9 ? '9+' : unread}
                </span>
              )}
            </button>
            {user ? (
              <button className="border-0 bg-transparent" onClick={() => navigate('/profile')}>
                <Avatar src={profile?.photoURL || user.photoURL} name={authorName(profile || user)} />
              </button>
            ) : (
              <Button variant="secondary" className="hidden sm:inline-flex" onClick={signIn}>Sign in</Button>
            )}
            {user && (
              <Button
                className="hidden shrink-0 sm:inline-flex sm:px-3.5"
                onClick={() => navigate('/community/write')}
              >
                + Add Post
              </Button>
            )}
            <ThemeToggle />
          </div>
        </header>
        <div className="px-4 py-5 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
          <PageSection />
        </div>
      </section>
      {showUsernamePrompt && (
        <UsernameClaimModal
          uid={user.uid}
          required
        />
      )}
    </div>
  );
}
